import { Constants } from '@constants';
import { differenceInDays, differenceInMonths } from 'date-fns';

import { TApplication, TTopUpStatus } from './getTopUpStatus.types';
import { TAccount, TAccountFormalOffer } from '../Accounts/accounts.types';
import { getInLoanDrawdownOffer } from '../Accounts/getInLoanDrawdownOffer';

/**
 * Calculates the topup status of a particular account.
 *
 * @param account An account object
 * @param application An application object
 * @returns The topup status of the account- can be 'NOT_YET_TOPUP_ELIGIBLE'| 'TOPUP_PENDING'| 'TOPUP_DECLINED'| 'TOPUP_ELIGIBLE'| 'TOPUP_APPROVED'| 'PRE_APPROVED'| 'INTEREST_ONLY_EXTENSION_APPROVED'| 'INTEREST_ONLY_EXTENSION_APPROVED_WITH_MORE_FUNDS'| undefined;
 */
export const getTopUpStatus = (
    account: TAccount,
    application: TApplication
): TTopUpStatus => {
    const isNotYetTopUpEligible = canTopUpAfterRepayments(account);
    const topUpPending = isTopUpPending(application, account);
    const canTopUpNow = canRequestTopUp(account, application);
    const topUpDeclined = application?.status === 'declined';
    const preApproved = isPreApproved(account);
    const topUpApproved = isTopUpApproved(account);
    let hasDeclineExpired;

    if (application) {
        const statusDate = application.status_date!;
        const declineStatus = application.decline_status;

        const monthsSinceStatusChange = differenceInMonths(
            new Date(),
            statusDate
        );

        if (
            (declineStatus === 'Soft Declined' &&
                monthsSinceStatusChange >= 3) ||
            (declineStatus === 'Hard Declined' && monthsSinceStatusChange >= 6)
        ) {
            hasDeclineExpired = true;
        }
    }

    const interestOnlyExtensionApproved =
        hasInterestOnlyExtensionApproval(account);

    if (preApproved) {
        if (topUpPending) {
            return 'TOPUP_PENDING';
        }

        if (topUpDeclined && !hasDeclineExpired) {
            return 'TOPUP_DECLINED';
        }

        return 'PRE_APPROVED';
    }

    if (interestOnlyExtensionApproved) {
        if (hasAvailableCash(account)) {
            return 'INTEREST_ONLY_EXTENSION_APPROVED_WITH_MORE_FUNDS';
        }

        return 'INTEREST_ONLY_EXTENSION_APPROVED';
    }

    if (topUpApproved) return 'TOPUP_APPROVED';
    if (topUpPending) return 'TOPUP_PENDING';
    if (isNotYetTopUpEligible) return 'NOT_YET_TOPUP_ELIGIBLE';
    if (topUpDeclined && !hasDeclineExpired) return 'TOPUP_DECLINED';
    if (canTopUpNow) return 'TOPUP_ELIGIBLE';

    return undefined;
};

const isTopUpApproved = (account: TAccount) => {
    if (hasInterestOnlyExtensionApproval(account)) return false;

    return (
        isLatestFormalOfferValid(account.latest_formal_offer) &&
        !hasApprovalExpired(account.latest_formal_offer) &&
        (hasAvailableCash(account) || hasCashlessOffer(account))
    );
};

const isPreApproved = (account: TAccount) => {
    const latestFormalOffer = account.latest_formal_offer;

    return (
        latestFormalOffer?.is_pre_approval &&
        isTopUpEligible(account) &&
        isTopUpApproved(account)
    );
};

const isLatestFormalOfferValid = (
    latestFormalOffer: TAccount['latest_formal_offer']
) => {
    return latestFormalOffer.is_valid;
};
const hasApprovalExpired = (
    latestFormalOffer: TAccount['latest_formal_offer']
) => {
    return (
        differenceInDays(new Date(latestFormalOffer.timeout!), new Date()) < 0
    );
};
const isTopUpEligible = (account: TAccount) => {
    return account.topup_eligibility?.is_allowed;
};

const canRequestTopUp = (account: TAccount, application: TApplication) => {
    return (
        canTopUpNow(account) &&
        !isTopUpPending(application, account) &&
        (hasApprovalExpired(account) || !hasAvailableCash(account))
    );
};

const canTopUpAfterRepayments = (account: TAccount) => {
    return (
        !canTopUpNow(account) &&
        isTopUpEligible(account) &&
        !hasAvailableCash(account)
    );
};
const canTopUpNow = (account: TAccount) => {
    return (
        isTopUpEligible(account) &&
        account.topup_eligibility?.amount_to_repay_before_topup === 0
    );
};

const isTopUpPending = (application: TApplication, account: TAccount) => {
    return (
        application?.status === 'open' &&
        account.product_type ===
            application.requested_products?.[0].product_type!
    );
};
const hasAvailableCash = (account: TAccount) => {
    const drawdownOffer = getInLoanDrawdownOffer(account);

    if (!drawdownOffer) {
        const hasNewFormalOffersWithAvailableCash = account.formal_offers.some(
            (offer: TAccountFormalOffer) => offerHasAvailableCash(offer)
        );

        return hasNewFormalOffersWithAvailableCash;
    }

    return offerHasAvailableCash(drawdownOffer);
};

const offerHasAvailableCash = (offer: TAccountFormalOffer) => {
    return offer.available_cash
        ? offer.available_cash >= Constants.MIN_DRAW_DOWN_AMOUNT
        : false;
};

const hasCashlessOffer = (account: TAccount) => {
    const hasCashlessFormalOffer = account.formal_offers.some(
        (offer: TAccountFormalOffer) => offer.is_repayment_reduction
    );

    return hasCashlessFormalOffer;
};

const hasInterestOnlyExtensionApproval = (account: TAccount): boolean => {
    //Interest Only Extensions are determined by:
    // 1. formal_offer is an interest only offer
    // 2. formal_offer offer_id is different to the offer that's been drawn down on
    // 3. formal_offer is still valid

    if (account) {
        const interestOnlyOffer = account.formal_offers?.find(
            (offer: TAccountFormalOffer) =>
                offer.interest_only_repayments &&
                offer.interest_only_repayments === 3 &&
                offer.is_repayment_reduction === false
        );

        if (!interestOnlyOffer) return false;

        const currentOffer = account.loan_offer_id;

        const fundedOffer = account.formal_offers.find(
            (offer: TAccountFormalOffer) => offer.offer_id === currentOffer
        );

        const expiredOffer = account.formal_offers.find(
            (offer: TAccountFormalOffer) => !offer.is_valid
        );
        if (expiredOffer || fundedOffer) return false;

        return Boolean(interestOnlyOffer.is_valid);
    }

    return false;
};
